<template>
    <div class="container-nzidoo">
        <page-title :title="$t('menu.myWallet')" />

        <!-- Wallet Stats Cards -->
        <div class="wallet-stats-grid">
            <div class="wallet-stat-card stat-revenue">
                <div class="stat-icon">
                    <i class="mdi mdi-arrow-up-bold"></i>
                </div>
                <div class="stat-content">
                    <h2 class="stat-value">
                        {{
                            wallet["total_amount_won"]
                            | currency(" DA", 0, { symbolOnLeft: false })
                        }}
                    </h2>
                    <p class="stat-label">{{ $t("wallet.revenue") }}</p>
                </div>
            </div>

            <div class="wallet-stat-card stat-expenses">
                <div class="stat-icon">
                    <i class="mdi mdi-arrow-down-bold"></i>
                </div>
                <div class="stat-content">
                    <h2 class="stat-value">
                        {{
                            wallet["total_amount_spent"]
                            | currency(" DA", 0, { symbolOnLeft: false })
                        }}
                    </h2>
                    <p class="stat-label">{{ $t("wallet.expenses") }}</p>
                </div>
            </div>

            <div class="wallet-stat-card stat-balance">
                <div class="stat-icon">
                    <i class="mdi mdi-wallet"></i>
                </div>
                <div class="stat-content">
                    <h2 class="stat-value">
                        {{
                            wallet["unpaid_amount"]
                            | currency(" DA", 0, { symbolOnLeft: false })
                        }}
                    </h2>
                    <p class="stat-label">{{ $t("wallet.balance") }}</p>
                </div>
            </div>

            <div class="wallet-stat-card stat-cashed">
                <div class="stat-icon">
                    <i class="mdi mdi-cash"></i>
                </div>
                <div class="stat-content">
                    <h2 class="stat-value">
                        {{
                            (wallet["total_amount_won"] - wallet["unpaid_amount"])
                            | currency(" DA", 0, { symbolOnLeft: false })
                        }}
                    </h2>
                    <p class="stat-label">{{ $t("wallet.cashedIn") }}</p>
                </div>
            </div>
        </div>

        <!-- Action Button -->
        <div class="wallet-action-section">
            <button @click="show('request-money')" class="btn btn-primary btn-lg">
                <i class="mdi mdi-cash-plus"></i>
                {{ $t("wallet.requestPayment") }}
            </button>
        </div>

        <!-- Payment Requests Section -->
        <div class="payment-requests-section">
            <div class="section-header">
                <h4>{{ $t("wallet.listPaymentRequest") }}</h4>
                <span class="requests-count" v-if="paymentRequest.length > 0">
                    {{ paymentRequest.length }} demande(s)
                </span>
            </div>

            <!-- Payment Requests Grid -->
            <div class="admin-grid" v-if="paymentRequest.length !== 0">
                <div v-for="payment in paymentRequest" :key="payment.id" class="admin-card payment-card">
                    <div class="admin-card-header">
                        <div class="payment-amount">
                            <i class="mdi mdi-cash-multiple"></i>
                            <span class="amount-value">
                                {{
                                    payment.montant
                                    | currency(" DA", 0, { symbolOnLeft: false })
                                }}
                            </span>
                        </div>
                        <div class="payment-status">
                            <span v-if="payment.state === null" class="status-badge status-pending">
                                <i class="mdi mdi-clock-outline"></i>
                                {{ $t("main.onHold") }}
                            </span>
                            <span v-if="payment.state == 0" class="status-badge status-refused">
                                <i class="mdi mdi-close-circle"></i>
                                {{ $t("payment.transfer.refused") }}
                            </span>
                            <span v-if="payment.state == 1" class="status-badge status-done">
                                <i class="mdi mdi-check-circle"></i>
                                {{ $t("payment.transfer.done") }}
                            </span>
                        </div>
                    </div>
                    <div class="admin-card-body">
                        <div class="admin-card-section">
                            <label class="admin-card-label">{{ $t("attr.date") }}</label>
                            <div class="card-name">
                                <i class="mdi mdi-calendar"></i>
                                {{ payment.created_at | moment }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="admin-empty-state">
                <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                    <line x1="2" y1="10" x2="22" y2="10"></line>
                </svg>
                <p class="admin-empty-text">{{ $t("sentence.noRequestHasBeenMade") }}</p>
            </div>
        </div>

        <request-money-modal :unpaidAmount="parseFloat(wallet['unpaid_amount'])"></request-money-modal>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import RequestMoneyModal from "@/components/modals/RequestMoneyModal";

export default {
    components: {
        PageTitle,
        "request-money-modal": RequestMoneyModal
    },
    name: "UserWallet",

    data() {
        return {
            wallet: [],
            paymentRequest: [],
        };
    },

    mounted() {
        this.getWallet();
    },

    methods: {
        show(modalName) {
            this.$modal.show(modalName);
        },

        async getWallet() {
            try {
                const response=await this.$axios.get("user/wallet");
                this.wallet=response.data;
                this.paymentRequest=response.data.payment_request;
            } catch (err) {
                console.error("Error loading wallet:", err);
                this.$notify({
                    group: "error",
                    title: "Erreur lors du chargement du portefeuille",
                    type: "error",
                });
            }
        },
    }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Wallet Stats Grid */
.wallet-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
    margin-bottom: 32px;
}

.wallet-stat-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s, box-shadow 0.2s;
}

.wallet-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    font-size: 32px;
}

.stat-revenue .stat-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
}

.stat-expenses .stat-icon {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
}

.stat-balance .stat-icon {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
}

.stat-cashed .stat-icon {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
}

.stat-content {
    flex: 1;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    color: var(--gray-800);
    margin: 0 0 4px 0;
    line-height: 1.2;
}

.stat-label {
    font-size: 14px;
    color: var(--gray-600);
    margin: 0;
    font-weight: 500;
}

/* Action Section */
.wallet-action-section {
    margin-bottom: 40px;
    display: flex;
    justify-content: center;
}

.btn-lg {
    padding: 14px 32px;
    font-size: 16px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.btn-lg i {
    font-size: 20px;
}

/* Payment Requests Section */
.payment-requests-section {
    margin-top: 48px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.section-header h4 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: var(--gray-800);
}

.requests-count {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: var(--blue-50);
    color: var(--blue-700);
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
}

/* Payment Card Specific Styles */
.payment-card .admin-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.payment-amount {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--gray-700);
}

.payment-amount i {
    font-size: 24px;
    color: var(--blue-600);
}

.amount-value {
    font-size: 18px;
    font-weight: 700;
}

.payment-status {
    display: flex;
    align-items: center;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
}

.status-pending {
    background: var(--orange-50);
    color: var(--orange-700);
    border: 1px solid var(--orange-200);
}

.status-refused {
    background: var(--red-50);
    color: var(--red-700);
    border: 1px solid var(--red-200);
}

.status-done {
    background: var(--green-50);
    color: var(--green-700);
    border: 1px solid var(--green-200);
}

.status-badge i {
    font-size: 16px;
}

.card-name {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--gray-600);
}

.card-name i {
    font-size: 16px;
    color: var(--gray-500);
}

/* Responsive Design */
@media (max-width: 768px) {
    .wallet-stats-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .stat-value {
        font-size: 20px;
    }

    .stat-icon {
        width: 56px;
        height: 56px;
        font-size: 28px;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
}

@media (max-width: 480px) {
    .wallet-stat-card {
        padding: 16px;
        gap: 12px;
    }

    .stat-icon {
        width: 48px;
        height: 48px;
        font-size: 24px;
    }

    .stat-value {
        font-size: 18px;
    }

    .stat-label {
        font-size: 13px;
    }
}
</style>